import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { OrderedMap } from 'immutable';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UniversalAssociationsSelect from 'universal-associations-select/components/UniversalAssociationsSelect';
import { parseUniversalEngagementAssociations
// @ts-expect-error module not typed
} from 'universal-associations-select/helpers/parseUniversalEngagementAssociations';
// @ts-expect-error module not typed

// @ts-expect-error module not typed

import { updateUniversalAssociation } from 'conversations-calling-message/timeline-call-metadata/associations/clients/updateUniversalAssociation';
import { FETCH_UNIVERSAL_ENGAGEMENT_ASSOCIATIONS } from 'conversations-calling-message/timeline-call-metadata/associations/queries/fetchUniversalEngagementAssociations';
// @ts-expect-error module not typed
import ProvideReferenceResolvers from 'reference-resolvers/ProvideReferenceResolvers';
// @ts-expect-error module not typed
import { CUSTOM_OBJECT } from 'reference-resolvers/constants/ReferenceObjectTypes';
// @ts-expect-error module not typed
import CustomObjectReferenceResolver from 'reference-resolvers/resolvers/CustomObjectReferenceResolver';
import { CALL_TYPE_ID, CONTACT_TYPE_ID, ObjectTypeFromIds } from 'customer-data-objects/constants/ObjectTypeIds';
import { CALL } from 'customer-data-objects/engagement/EngagementTypes';
import { INCOMING } from 'conversations-message-history/common-message-format/constants/messageDirections';
import { getMessageDirection, getSenderIdFromMessage, getSingleRecipientIdFromMessage } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import { TICKET } from 'customer-data-objects/constants/ObjectTypes';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledWrapper = styled.div.attrs({
  className: 'p-bottom-1'
}).withConfig({
  displayName: "AssociationsSelectWrapper__StyledWrapper",
  componentId: "sc-1xmkdoc-0"
})(["display:flex;flex-direction:row-reverse;"]);
const getErrorTitleText = () => /*#__PURE__*/_jsx(FormattedMessage, {
  message: "conversations-calling-message.timeline-call-metadata.universal-associations-select.error.title"
});
const getErrorMessageText = () => /*#__PURE__*/_jsx(FormattedMessage, {
  message: "conversations-calling-message.timeline-call-metadata.universal-associations-select.error.message"
});
const AssociationsSelectWrapper = ({
  alertStore,
  callMessage,
  message
}) => {
  const [associationRecords, setAssociationRecords] = useState(OrderedMap());
  const engagementId = callMessage.crmObjectId;
  const direction = getMessageDirection(message);
  const subjectObjectId = direction === INCOMING ? getSenderIdFromMessage(message) : getSingleRecipientIdFromMessage(message);
  const variables = useMemo(() => {
    return {
      engagementExists: !!engagementId,
      engagementObjectId: engagementId,
      engagementObjectTypeId: CALL_TYPE_ID,
      isTicket: ObjectTypeFromIds[CONTACT_TYPE_ID] === TICKET,
      subjectObjectId,
      subjectObjectTypeId: CONTACT_TYPE_ID
    };
  }, [engagementId, subjectObjectId]);
  const {
    loading,
    error,
    data
  } = useQuery(FETCH_UNIVERSAL_ENGAGEMENT_ASSOCIATIONS, {
    variables
  });
  useEffect(() => {
    if (loading || error) {
      return;
    }
    const parsedRecords = parseUniversalEngagementAssociations({
      data,
      variables
    });
    setAssociationRecords(parsedRecords);
  }, [data, error, loading, variables]);
  const currentUserCanCommunicateWithEngagement = Boolean(data && data.engagement && data.engagement.userPermissions && data.engagement.userPermissions.currentUserCanCommunicate);
  const canViewEngagement = useMemo(() => {
    const isAuthorizationError = Boolean(error && error.message && error.message.includes('AuthorizationException'));
    return !isAuthorizationError;
  }, [error]);
  const handleSelectAssociation = useCallback((associationRecord, associationOptionRecord) => {
    const optionId = `${associationOptionRecord.get('objectId')}`;
    const toObjectTypeId = associationRecord.get('toObjectTypeId');

    // Grab the associationRecord from associationRecords to ensure all current associations persist
    const currentAssociationRecord = associationRecords.get(toObjectTypeId);
    const updatedAssociationRecord = currentAssociationRecord.updateIn(['associationOptionRecords'], optionRecords => {
      const optionRecord = optionRecords.get(optionId);
      if (optionRecord) {
        return optionRecords.setIn([optionId, 'isSelected'], true);
      }
      return optionRecords.set(optionId, associationOptionRecord.set('isSelected', true));
    });
    updateUniversalAssociation({
      engagementId: engagementId,
      objectId: optionId,
      updatedAssociationRecord,
      isDisassociation: false
    }).then(() => {
      setAssociationRecords(associationRecords.setIn([toObjectTypeId, 'associationOptionRecords', optionId], associationOptionRecord.set('isSelected', true)));
    }).catch(() => {
      alertStore.addAlert({
        message: getErrorMessageText(),
        titleText: getErrorTitleText(),
        type: 'danger'
      });
    });
  }, [alertStore, associationRecords, engagementId]);
  const handleUnselectAssociation = useCallback((associationRecord, associationOptionRecord) => {
    const optionId = `${associationOptionRecord.get('objectId')}`;
    const toObjectTypeId = associationRecord.get('toObjectTypeId');
    // Grab the associationRecord from associationRecords to ensure all current associations persist
    const currentAssociationRecord = associationRecords.get(toObjectTypeId);
    const updatedAssociationRecord = currentAssociationRecord.updateIn(['associationOptionRecords'], optionRecords => {
      const optionRecord = optionRecords.get(optionId);
      if (optionRecord.get('isDefaultAssociation')) {
        return optionRecords.setIn([optionId, 'isSelected'], false);
      }
      return optionRecords.delete(optionId);
    });
    updateUniversalAssociation({
      engagementId: engagementId,
      objectId: optionId,
      updatedAssociationRecord,
      isDisassociation: true
    }).then(() => {
      setAssociationRecords(associationRecords.setIn([toObjectTypeId, 'associationOptionRecords', optionId], associationOptionRecord.set('isSelected', false)));
    }).catch(() => {
      alertStore.addAlert({
        message: getErrorMessageText(),
        titleText: getErrorTitleText(),
        type: 'danger'
      });
    });
  }, [alertStore, associationRecords, engagementId]);
  return /*#__PURE__*/_jsx(StyledWrapper, {
    children: /*#__PURE__*/_jsx(UniversalAssociationsSelect, {
      associationRecords: associationRecords,
      canEdit: true,
      canView: canViewEngagement,
      currentUserCanCommunicateWithEngagement: currentUserCanCommunicateWithEngagement,
      engagementOrInteractionType: CALL,
      isError: !!error,
      isLoading: loading,
      onSelectAssociation: handleSelectAssociation,
      onUnselectAssociation: handleUnselectAssociation,
      popoverLabelKey: 'conversations-calling-message.timeline-call-metadata.universal-associations-select.label',
      skeletonStateWidth: 100
    })
  });
};

// Required for UniversalAssociationsSelect
const resolvers = {
  [CUSTOM_OBJECT]: resolversCache => () => ({
    resolversCache,
    resolverFactory: CustomObjectReferenceResolver
  })
};
export default ProvideReferenceResolvers(resolvers)(AssociationsSelectWrapper);